/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useEffect, useState} from "react";
import {AdalConfig, Authentication, AuthenticationContext} from "adal-ts";
import {AxiosResponse} from "axios";
import {IJCoreService, IUserId} from "../../services/jcore";
import {adalConfig} from "../../adalConfig";
import {IAnalyticsService} from "../../services/analytics";

interface IAuthContext {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isAzureAccount: (
    username: string,
    analytics?: IAnalyticsService
  ) => Promise<{ isAzure: boolean; isRegistered: boolean }>;
  error: null | {
    type: string;
    body: string;
  };
  isFirstLogin?: boolean;
  adal?: AuthenticationContext;
  login: (
    username: string,
    password: string,
    recaptchaToken: string,
    rememberMe?: boolean
  ) => Promise<IUserId>;
  validateToken: (token: string) => Promise<IUserId>;
  getAzureTokenFunction: () => string;
  sendRecoveryEmail: (email: string) => Promise<AxiosResponse<boolean>>;
  validateRecoveryToken: (token: string) => Promise<boolean>;
  validateAzureInvitationToken: (token: string) => void;
  resetPassword: (
    token: string,
    password: string,
    resetApp?: string
  ) => Promise<AxiosResponse<boolean>>;
  verifyAccount: (token: string) => Promise<AxiosResponse<boolean>>;
  unsubscribe: (token: string) => void;
  setAzureToken: (token: string) => void;
  getAzureToken: () => any;
  logout: () => void;
}

const mockAuth: IAuthContext = {
  isAuthenticating: false,
  isAuthenticated: false,
  async isAzureAccount(username: string, analytics?: IAnalyticsService) {
    return new Promise(resolve =>
      setTimeout(() => resolve({ isAzure: false, isRegistered: true }), 300)
    );
  },
  error: null,
  async login(username: string, password: string) {
    this.isAuthenticating = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    this.isAuthenticating = false;
    this.isAuthenticated = true;
    return "";
  },
  validateToken() {
    return new Promise(resolve => setTimeout(resolve, 300));
  },
  sendRecoveryEmail() {
    return new Promise(resolve => setTimeout(resolve, 300));
  },
  validateRecoveryToken() {
    return new Promise(resolve => setTimeout(resolve, 300));
  },
  resetPassword() {
    return new Promise(resolve => setTimeout(resolve, 300));
  },
  verifyAccount() {
    return new Promise(resolve => setTimeout(resolve, 300));
  },
  logout() {
    this.isAuthenticated = false;
  },
  unsubscribe() {},
  getAzureToken() {
    return "";
  },
  setAzureToken() {
    return "";
  },
  getAzureTokenFunction() {
    return "";
  },
  validateAzureInvitationToken(token: string) {
    return "";
  }
};

export const useAuthContext: (jcore: IJCoreService, handleLogout?: () => void) => IAuthContext = (
  jcore: IJCoreService,
  handleLogout?: () => void
) => {
  const config = new AdalConfig(
    adalConfig.clientId,
    adalConfig.tenant,
    window.location.origin,
    window.location.origin,
    "token",
    `resource=${encodeURIComponent("00000002-0000-0000-c000-000000000000")}`
  );
  const adal = Authentication.getContext(config);
  const getAzureTokenFunction = () => adal.getToken();
  const [isAuthenticated, setAuthenticated] = useState(jcore.isLoggedIn());
  const [isAuthenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState<null | {
    type: string;
    body: string;
  }>(null);
  const [isFirstLoginVal, setFirstLogin] = useState(jcore.isFirstLogin());

  /* Check if adal.getToken returns anything, if it does, do azureauth request */
  const azureToken = getAzureTokenFunction();

  const refAzureToken = useCallback(() => {
    if (azureToken !== null && azureToken !== "") {
      const ddmInvitationToken = localStorage.getItem("ddm-azureinvitationtoken");
      if (!ddmInvitationToken) {
        setAuthenticating(true);
        jcore
          .azureAuth(azureToken)
          .then((d: any) => {
            const token = d.replace("Bearer ", "") as string;
            jcore.setUserToken(token);
            setAuthenticated(true);
          })
          .catch(() => {
            const user = adal.getUser();
            setError({
              type: "NO_ACCESS_AZURE",
              body: user && user.unique_name
            });
            setAuthenticated(false);
          })
          .finally(() => {
            setAuthenticating(false);
            localStorage.removeItem("adal.accesstoken");
            localStorage.removeItem("ddm-azureinvitationtoken");

            setTimeout(() => {
              setError(null);
            }, 100);
          });
      }
    }
  }, [adal, azureToken, jcore]);

  useEffect(() => {
    refAzureToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureToken]);

  const isAzureAccount = async (username: string, analytics?: IAnalyticsService) => {
    let isAzure = false;
    let isRegistered;

    try {
      const { data } = await jcore.isAzureAccount(username);
      isAzure = data.azure;
      isRegistered = username === data.username;
    } catch (e) {
      isRegistered = false;
      analytics?.logEvent("USERNAME_VALIDATION_ERROR", username);
    }

    return { isAzure, isRegistered };
  };

  const login = async (
    username: string,
    password: string,
    recaptchaToken: string,
    rememberMe?: boolean
  ) => {
    let userId = "";
    setAuthenticating(true);
    jcore.deleteFirstLoginToken();
    try {
      const { isFirstLogin, id } = await jcore.login({
        username: username.trim(),
        password,
        recaptchaToken,
        rememberMe
      });
      setFirstLogin(isFirstLogin);
      userId = id;
      setAuthenticated(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error login", e);
      throw e;
    } finally {
      setAuthenticating(false);
    }
    return userId;
  };

  const validateToken = async (token: string) => {
    let userId = "";
    setAuthenticating(true);
    try {
      const { isFirstLogin, id } = await jcore.validateToken(token);
      setFirstLogin(isFirstLogin);
      userId = id;
      setAuthenticated(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("error validating token", e);
      throw e;
    } finally {
      setAuthenticating(false);
    }
    return userId;
  };

  const validateRecoveryToken = async (token: string) => {
    try {
      const result = await jcore.validateRecoveryToken(token);
      return result.data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("error validating recovery token", e);

      throw e;
    }
  };

  const validateAzureInvitationToken = async (token: string) => {
    try {
      const result = await jcore.linkAzureAccount(token, getAzureTokenFunction());
      localStorage.removeItem("ddm-azureinvitationtoken");
      refAzureToken();
      return result;
    } catch (e) {
      const user = adal.getUser()?.unique_name;
      let text = "error";

      try {
        const azure = await jcore.isAzureAccount(user);

        if (azure?.data?.azure) {
          text = "registered";
        }
      } catch {
        // eslint-disable-next-line no-console
        console.log("Account not registered in azure");
      }

      setError({
        type: "ERROR_LINKING_ACCOUNT",
        body: text
      });
    } finally {
      localStorage.removeItem("adal.accesstoken");
      localStorage.removeItem("ddm-azureinvitationtoken");

      setTimeout(() => {
        setError(null);
      }, 100);
    }
  };

  const sendRecoveryEmail = async (email: string) => {
    try {
      return await jcore.sendRecoveryEmail(email);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  };

  const resetPassword = async (token: string, password: string, resetApp?: string) => {
    try {
      return await jcore.resetPassword(token, password, resetApp);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  };

  const verifyAccount = async (token: string) => {
    try {
      return await jcore.verifyAccount(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  };

  const logout = () => {
    jcore.logout();
    setAuthenticated(false);
    if (handleLogout) {
      handleLogout();
    }
  };

  const unsubscribe = (token: string) => {
    try {
      jcore.unsubscribe(token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const getAzureToken = () => jcore.getAzureToken();
  const setAzureToken = (token: string) => {
    jcore.setAzureToken(token);
  };

  return {
    isAuthenticated,
    isAuthenticating,
    adal,
    isFirstLogin: isFirstLoginVal,
    validateToken,
    getAzureToken,
    setAzureToken,
    getAzureTokenFunction,
    validateAzureInvitationToken,
    validateRecoveryToken,
    sendRecoveryEmail,
    resetPassword,
    verifyAccount,
    unsubscribe,
    login,
    isAzureAccount,
    logout,
    error
  };
};

export const AuthContext = React.createContext<IAuthContext>(mockAuth);
export const AuthProvider = AuthContext.Provider;
export const AuthConsumer = AuthContext.Consumer;
export default AuthContext;
